import { useState } from 'react';
import { Box, Button, TableCell, TableRow, Tooltip, Stack, Typography, Checkbox, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import moment from 'moment';
import { dateMonthYearDateFormat } from '../../../constants';
import NumericLabel from 'react-pretty-numbers';
import { trackActivity } from '../../../utils/mixpanel';
import { PermissionWrapper } from '../../../wrappers';
import LessMoreComp from '../../../shared_elements/components/CustomLessMoreComp';
import AuditPopover from '../../../shared_elements/components/popover';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestoreIcon from '@mui/icons-material/Restore';

const CHANGEABLE_STATUSES = [1, 2, 5];
const DELETABLE_STATUSES = [1, 2, 4];

const SummaryInvoiceList = ({ item, onDelete, onView, onInvStatus, viewRevisonHistory, isQbEnabled, isSelected, canSelect, onToggleSelect }) => {
    const status = item?.status || { value: null, label: '--' };
    const [showAllAssets, setShowAllAssets] = useState(false);

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Summary Invoices', page_type });
    };

    return (
        <TableRow>
            <TableCell className="table-actions-list">
                <Box className="icon-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isQbEnabled && (
                        <Tooltip title={!canSelect ? (item?.qb_last_sync_by ? 'Already synced to QB' : 'Only Send status invoices can be synced') : ''} arrow>
                            <span>
                                <Checkbox
                                    size="small"
                                    checked={!!isSelected}
                                    disabled={!canSelect}
                                    onChange={onToggleSelect}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </span>
                        </Tooltip>
                    )}
                    <AuditPopover item={item}>
                        <MoreVertIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }} />
                    </AuditPopover>
                    <Tooltip title="Revision History">
                        <RestoreIcon className='revision-history' color="primary" fontSize='small' sx={{ cursor: 'pointer' }} onClick={() => { viewRevisonHistory(); handleItemClicked('RevisionHistoryIcon'); }} />
                    </Tooltip>
                    <PermissionWrapper extraProps={{ application: 'lease_revamp', feature: 'invoices', operationType: ['R'] }}>
                        <Tooltip title="View" arrow>
                            <VisibilityIcon
                                fontSize="small"
                                color="primary"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => { onView(); handleItemClicked('ViewIcon'); }}
                            />
                        </Tooltip>
                    </PermissionWrapper>
                    {DELETABLE_STATUSES.includes(status.value) && !item.qb_last_sync_by &&  (
                        <PermissionWrapper extraProps={{ application: 'lease_revamp', feature: 'invoices', operationType: ['D'] }}>
                            <Tooltip arrow title="Delete">
                                <DeleteIcon
                                    fontSize="small"
                                    color="error"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => { onDelete(); handleItemClicked('DeleteIcon'); }}
                                />
                            </Tooltip>
                        </PermissionWrapper>
                    )}
                </Box>
            </TableCell>

            <TableCell>
                {!item?.assets?.length ? '--' : (
                    <>
                        {(() => {
                            const sorted = [...item.assets].sort((a, b) => (a.asset_type ?? 0) - (b.asset_type ?? 0));
                            return showAllAssets
                                ? sorted.map((a, i) => <div key={i}>{a.asset_name}</div>)
                                : <div>{sorted[0].asset_name}</div>;
                        })()}
                        {item.assets.length > 1 && (
                            <span
                                style={{ color: '#1976d2', cursor: 'pointer', fontSize: '12px' }}
                                onClick={(e) => { e.stopPropagation(); setShowAllAssets(v => !v); }}
                            >
                                {showAllAssets ? 'View Less' : 'View More'}
                            </span>
                        )}
                    </>
                )}
            </TableCell>

            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data'); }}>
                {item?.invoice_number || '--'}
                <div style={{ fontSize: '10px', height: '18px' }}>{item?.invoice_type?.name || '--'}</div>
                {item?.qb_last_sync_by && (<div><Chip sx={{ fontSize: '10px', height: '18px', marginBottom:'2px' }} label="QB Synced" size="small" color="success" variant="outlined" /></div>)}
                {item?.is_advanced && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="Advanced" size="small" variant="outlined" /> </div>)}
                {item?.is_arrears && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="Arrears" size="small" variant="outlined" /> </div>)}
            </TableCell>

            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data'); }}>
                <div style={{ width: item?.lessor?.name?.length > 20 ? '200px' : '' }}>
                    <LessMoreComp name={item?.lessor?.name || '--'} lessMr='Lessor' />
                </div>
            </TableCell>

            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data'); }}>
                <div style={{ width: item?.lessee?.name?.length > 20 ? '200px' : '' }}>
                    <LessMoreComp name={item?.lessee?.name || '--'} lessMr='Lessee' />
                </div>
            </TableCell>

            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data'); }}>
                {item?.date ? moment(item.date).format(dateMonthYearDateFormat) : '--'}
            </TableCell>

            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data'); }}>
                {item?.due_date ? moment(item.due_date).format(dateMonthYearDateFormat) : '--'}
            </TableCell>

            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data'); }}>
                {item?.start_date && item?.end_date
                    ? `${moment(item.start_date).format(dateMonthYearDateFormat)} - ${moment(item.end_date).format(dateMonthYearDateFormat)}`
                    : '--'}
            </TableCell>

            <TableCell>
                <Button
                    variant="contained"
                    className={`invoice-status-${status.value}`}
                    disabled={!CHANGEABLE_STATUSES.includes(status.value) || item?.qb_last_sync_by}
                    sx={{
                        textTransform: 'capitalize',
                        padding: '0 6px',
                        fontWeight: '600',
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                        width: '120px',
                        cursor: (CHANGEABLE_STATUSES.includes(status.value) && !item?.qb_last_sync_by) ? 'pointer' : 'not-allowed',
                        justifyContent: 'center',
                        borderRadius: '3px',
                        '& .MuiButton-endIcon': { marginLeft: '0px' },
                        '&.Mui-disabled': { cursor: 'not-allowed', pointerEvents: 'auto', opacity: 1 },
                    }}
                    onClick={() => CHANGEABLE_STATUSES.includes(status.value) ? onInvStatus() : null}
                    endIcon={CHANGEABLE_STATUSES.includes(status.value) ? <ArrowDropDownIcon /> : null}
                >
                    {status.label}
                </Button>
                {[5].includes(item?.status?.value) ? <Stack spacing={0}>
                    <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Partial Payment:</span> {item?.total_partial_paid_amount}</Typography>
                    <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Payment Date:</span> {item?.partial_payments?.[0]?.payment_date ?  moment(item?.partial_payments?.[0]?.payment_date).format(dateMonthYearDateFormat) : '--'}</Typography>
                    <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Balance Amount:</span> <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: item?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{item?.remaining_balance_amount || 0.00}</NumericLabel></Typography>
                    {item?.payment_late_by === 0 ? null : <Typography sx={{ color: "red", fontSize: "10px" }}>Payment late by {item?.payment_late_by} Day(s)</Typography>}
                </Stack> : [2].includes(item?.status?.value) ?
                    <Stack spacing={0}>
                        {item?.overdue_by !== 0 ? <Typography sx={{ color: "red", fontSize: "10px" }}>Overdue by {item?.overdue_by} Day(s)</Typography> : null}
                    </Stack> : [3].includes(item?.status?.value) ?
                        <Stack spacing={0}>
                            <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Payment Date:</span> {item?.paid_date ? moment(item.paid_date).format(dateMonthYearDateFormat) : '--'}</Typography>
                            {item?.payment_late_by === 0 ? null : <Typography sx={{ color: "red", fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Payment late by</span> {item?.payment_late_by} Day(s)</Typography>}
                        </Stack> : null}
            </TableCell>

            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data'); }}>
                <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: item?.currency, precision: 2, commafy: true, justification: 'L' }}>
                    {item?.total_amount || 0}
                </NumericLabel>
            </TableCell>
        </TableRow>
    );
};

export default SummaryInvoiceList;
